import React, { useEffect, useMemo, useState } from "react";
import { ListGroup } from "react-bootstrap";

import "./StatusListItemComponent.css";
import {
  CheckCircle,
  CheckCircleFill,
  ExclamationCircleFill,
  Radioactive,
  X,
  XCircleFill,
} from "react-bootstrap-icons";
import { StationStatus } from "../../utils/constants";

/**
 * Calculates which bands have interference based on frequencies and bandwidth.
 * @memberof module:components/lists
 * @param {Array<{name: string, centralFreq: number}>} bands - Array of band objects with central frequency.
 * @param {Array<{frequency: number}>} interferences - Array of interference objects with frequency.
 * @param {number} bandwidth - Bandwidth to check around each band's central frequency.
 * @returns {boolean[]} Array of booleans where each element indicates if the corresponding band has interference.
 */
const calculateInterferences = (bands, interferences) => {
  return bands?.map((band) =>
    interferences.some(
      (rfBand) =>
        rfBand.frequencyValue >= band.centralFreq - band.bandwidth / 2 &&
        rfBand.frequencyValue <= band.centralFreq + band.bandwidth / 2,
    ),
  );
};

/**
 * StatusListItem renders a single band item showing its interference status.
 * @memberof module:Lists
 * @param {Object} props
 * @param {{name: string, centralFreq: number}} props.band - The band object.
 * @param {boolean} props.isInterference - Whether the band has interference.
 * @returns {JSX.Element} The list item element showing the band status.
 */
const StatusListItem = ({ band, isInterference, status }) => {
  let variant = "secondary";

  switch (status.code) {
    case StationStatus.state.CONNECTING:
    case StationStatus.state.DISCONNECTED:
      return (
        <ListGroup.Item
          style={{ height: "3rem", display: "flex", alignItems: "center" }}
          id="groupList"
          variant={"secondary"}
        >
          <XCircleFill
            style={{
              flexShrink: 0,
              paddingRight: 10,
              width: "1.6rem",
              height: "1.6rem",
            }}
            className={`bi`}
          >
          </XCircleFill>
          <span>{`${band.name} not available`}</span>
        </ListGroup.Item>
      );
      case StationStatus.state.DATA_UNAVAILABLE:
      return (
        <ListGroup.Item
          style={{ height: "3rem", display: "flex", alignItems: "center" }}
          id="groupList"
          variant={"warning"}
        >
          <ExclamationCircleFill
            style={{
              flexShrink: 0,
              paddingRight: 10,
              width: "1.6rem",
              height: "1.6rem",
            }}
            className={`bi`}
          >
          </ExclamationCircleFill>
          <span>{`${band.name} unknown status`}</span>
        </ListGroup.Item>
      );
  }

  return(<ListGroup.Item
    style={{ height: "3rem", display: "flex", alignItems: "center" }}
    id="groupList"
    variant={
      isInterference ? "danger" : "success"
    }
  >
    {isInterference ? (
      <Radioactive
        style={{
          flexShrink: 0,
          paddingRight: 10,
          width: "1.6rem",
          height: "1.6rem",
        }}
        className={`bi blink`}
      ></Radioactive>
    ) : (
      <CheckCircleFill
        style={{
          flexShrink: 0,
          paddingRight: 10,
          width: "1.6rem",
          height: "1.6rem",
        }}
        className={`bi`}
      ></CheckCircleFill>
    )}

    <span>{`${band.name} band ${isInterference ? "has interference" : "is clean"}`}</span>
  </ListGroup.Item>)
};

/**
 * StatusListItemComponent renders a list of bands with their interference statuses.
 *
 * @param {Object} props
 * @param {Array<{name: string, centralFreq: number}>} props.bands - Array of band objects.
 * @param {Array<{frequency: number}>} props.interferences - Array of detected interference frequencies.
 * @param {number} props.bandwidth - Bandwidth used to detect interference within each band.
 * @returns {JSX.Element} The list of band status items.
 */
const StatusListItemComponent = ({ bands, interferences, stationStatus }) => {
  const bandsInterference = useMemo(
    () => calculateInterferences(bands, interferences || []),
    [bands, interferences],
  );

  return (
    <ListGroup
      style={{
        width: "100%",
        marginTop: "15px",
        marginLeft: "0px",
        marginRight: "0px",
        padding: 0,
      }}
      as="ul"
    >
      {bands?.map((band, index) => (
        <StatusListItem
          key={index}
          band={band}
          isInterference={bandsInterference[index]}
          status={stationStatus}
        />
      ))}
    </ListGroup>
  );
};

export default StatusListItemComponent;
